import { MutationCache, QueryClient } from "@tanstack/react-query";

import { ApiError } from "./api.js";

/**
 * The one query client, and the one place a lost session is noticed.
 *
 * `App` decides whether somebody is signed in from the `["me"]` query alone,
 * which was right for a page load and wrong for everything after it: a session
 * that expired or was revoked while the tab was open surfaced as an inline red
 * sentence under whichever form had just been submitted ("Sign in to
 * continue."), and the shell around it went on looking signed in. The person
 * had no way to know the remedy was to sign in again (LGN-126, carried from the
 * 2026-08-28 audit).
 *
 * A `MutationCache` error handler is the shared hook that fixes it: every
 * mutation in the application goes through this cache, so no screen has to
 * remember to opt in. Invalidating `["me"]` makes the query the shell already
 * watches refetch, get its own 401, and render the sign-in screen. The refusal
 * is still returned to the caller, so a screen that wants to say something
 * about the specific action it failed still can.
 *
 * Only 401. A 403 is a live session being told no, and answering it by
 * throwing somebody back to sign-in would be both wrong and a loop.
 */
export function createQueryClient(): QueryClient {
  // Assigned immediately below. The handler cannot run before then: it fires
  // only from a mutation, and there is no mutation until a component has one.
  let client: QueryClient | undefined;

  const mutationCache = new MutationCache({
    onError: (error) => {
      if (!(error instanceof ApiError) || !error.isUnauthenticated) return;
      void client?.invalidateQueries({ queryKey: ["me"] });
    },
  });

  client = new QueryClient({
    mutationCache,
    defaultOptions: {
      queries: {
        // The API is the source of truth and cheap to re-read; stale data in a
        // deploy dashboard is worse than an extra request.
        staleTime: 5_000,
        retry: (failureCount, error) =>
          // Never retry an auth failure into a loop: re-render the sign-in
          // screen.
          error instanceof Error && error.name === "ApiError" ? false : failureCount < 2,
        refetchOnWindowFocus: true,
      },
    },
  });

  return client;
}
